/* eslint-disable react-native/no-inline-styles */
/* eslint-disable react/prop-types */
/*
Author: Ayi Regiarty
*/

import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  View,
  StyleSheet,
  TouchableOpacity,
  Dimensions,
  ScrollView,
} from 'react-native';
import { formatISO, addDays, endOfDay, startOfDay } from 'date-fns';
import { isWeb, fullWidth, isMobileWeb, isAndroid } from '@global/screens';
import { parseDate, exportCsvApi } from '@global/helpers';
import Colors from '@global/colors';
import * as Layout from '@components/screen/Layout';
import Table from '@components/table/Table';
import TableMobile from '@components/table/TableMobile';
import SociollaText from '@components/sociolla/SociollaText';
import DatePicker from '@components/form/DatePicker';
import StatusPicker from '@components/form/StatusPicker';
import StatusBadge from '@components/global/StatusBadge';
import Image from '@components/global/Image';
import { showPopup } from '@/store/modal';
import SalesTeamSearch from '@components/form/search/SalesTeamSearch';
import SelectBox from '@components/form/SelectBox';
import ModalFull from '@components/modal/ModalFull';
import Button from '@components/form/Button';
import useApi from '@/hooks/useApi';
import { MsOrder } from '@api/ms-order';
import { filterAdded, paramsReseted } from '@/store/params';
import { showAlert } from '@/store/global';
import useListScreen from '@/hooks/useListScreen';
import {
  useGetLpbListQuery,
  useGetLpbCountQuery,
  useGetLpbInfiniteListInfiniteQuery,
} from '@api/listApi';

const dimension = Dimensions.get('screen');

// Roles allowed to see the Export button.
const EXPORT_ROLES = [
  'superadmin',
  'sales_admin',
  'branch_head',
  'regional_manager',
  'regional_support',
  'key_account_manager',
  'group_key_account_manager',
  'national_key_account_manager',
  'distributor_manager',
  'general_manager',
  'vice_president',
  'finance_supervisor',
  'spv_logistic',
  'vice_president_finance',
  'budget_controller',
  'finance_head',
  'tax_support',
  'billing_supervisor',
  'chief_of_staff',
  'general',
  '3pl',
  'regional_distributor_manager',
];

const tableStructure = {
  created_date: {
    head: 'Created Date',
    type: 'date',
    body: 'created_at',
  },
  sales_order: {
    head: 'SO Number',
    body: 'so_number',
  },
  delivery_order: {
    head: 'DO Number',
    body: 'do_number',
  },
  customer: {
    head: 'Customer',
    body: 'customer_name',
  },
  channel: {
    head: 'Channel',
    body: 'channel',
  },
  sales_team: {
    head: 'Sales Team',
    body: 'sale_team',
  },
  carrier: {
    head: 'Carrier',
    body: 'carrier_name',
  },
  status: {
    head: 'Status',
    body: 'status',
    type: 'status',
  },
};

// UI filter shape → API query filter (was the inline transform in the old finalParam):
//  - `keyword` is unwrapped from the search box's { $regex } object
//  - `sales_team` (array of team objects) → sales_team.id: { $in: [cerebro_id] }
//  - `carriers` (single object) → carrier_id
//  - `is_search` is set whenever any filter/search is applied (BE flag)
const toApiFilter = (rawFilter = {}) => {
  const filter = {};
  const kw = rawFilter.keyword?.$regex ?? rawFilter.keyword;
  if (kw) {
    filter.keyword = kw;
  }
  if (rawFilter.from_date) {
    filter.from_date = rawFilter.from_date;
  }
  if (rawFilter.to_date) {
    filter.to_date = rawFilter.to_date;
  }
  if (rawFilter.status) {
    filter.status = rawFilter.status;
  }
  if (rawFilter.sales_team?.length) {
    filter['sales_team.id'] = {
      $in: rawFilter.sales_team.map((el) => el.cerebro_id),
    };
  }
  if (rawFilter.carriers?.id) {
    filter.carrier_id = rawFilter.carriers.id;
  }
  if (Object.keys(filter).length) {
    filter.is_search = true;
  }
  return filter;
};

const statuses = [
  { name: 'Draft', value: 'draft' },
  { name: 'Need Approval', value: 'need_approval' },
  { name: 'Rejected', value: 'rejected' },
  { name: 'Approved', value: 'approved' },
  { name: 'Completed', value: 'completed' },
];

// Restrict the Sales Team picker to the user's own teams (or all if none assigned).
const salesTeamFilter = (userData) => {
  if (userData?.sales_team?.length) {
    return {
      _id: { $in: userData.sales_team.map((team) => team.id) },
    };
  } else {
    return {};
  }
};

const LPBScreen = (props) => {
  // ── selectors ──
  const userData = useSelector((state) => state.auth.user);
  const dispatch = useDispatch();
  const showLPBExport = useSelector((state) => state.modal.showLPBExport);
  const showFilter = useSelector((state) => state.modal.showFilter);

  // ── local state ──
  const [carriers, setCarriers] = useState([]);
  const [carriersLoading, setCarriersLoading] = useState(false);
  const [carriersParams, setCarriersParams] = useState({
    skip: 0,
    limit: 10,
    loading: false,
  });
  const [exporting, setExporting] = useState(false);
  const [filterExport, setFilterExport] = useState({
    from_date: '',
    to_date: '',
  });
  const [draftFilter, setDraftFilter] = useState({});
  const [draftCarrier, setDraftCarrier] = useState('');
  const [draftSalesTeam, setDraftSalesTeam] = useState('');

  const getLpbCarrier = useApi(MsOrder.getLpbCarrier);

  // ── list data ──
  const { list, count, openChild, scope, params } = useListScreen({
    listHook: useGetLpbListQuery,
    countHook: useGetLpbCountQuery,
    infiniteHook: useGetLpbInfiniteListInfiniteQuery,
    toFilter: toApiFilter,
    // GRN dashboard cards navigate here with a `presetFilter` param (status + period).
    presetFilter: props.route?.params?.presetFilter,
  });

  // ── memoized values ──
  const showExportButton = useMemo(
    () => EXPORT_ROLES.includes(userData?.role?.code),
    [userData],
  );

  // Current search text, derived from the applied keyword filter.
  const searchValue = useMemo(() => {
    const k = params.filter?.keyword;
    return k && k.$regex ? k.$regex : k ?? '';
  }, [params.filter]);

  // Web = flat array; mobile = paged infinite result. id is already mapped by the factory.
  const rows = useMemo(() => {
    return isWeb
      ? Array.isArray(list.data)
        ? list.data
        : []
      : (list.data?.pages ?? []).flat();
  }, [list.data]);

  // ── effects ──
  useEffect(() => {
    if (showFilter) {
      const applied = params.filter || {};
      setDraftFilter(applied);
      setDraftCarrier(applied.carriers || '');
      setDraftSalesTeam(applied.sales_team || '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showFilter]);

  // ── handlers ──
  const draftDateHandler = (value, type) => {
    const date = new Date(value);
    setDraftFilter((f) => {
      const next = { ...f };
      if (type === 'from_date') {
        const startDate = new Date(
          date.getFullYear(),
          date.getMonth(),
          date.getDate(),
        );
        next.from_date = { $gte: startDate.toISOString() };
      } else {
        const endDate = new Date(
          date.getFullYear(),
          date.getMonth(),
          date.getDate(),
          23,
          59,
          59,
        );
        next.to_date = { $lte: endDate.toISOString() };
      }
      return next;
    });
  };

  const goToDetail = (data) => {
    openChild('LPBDetailScreen', {
      titleScreen: 'Detail GRN',
      name: data.customer_name ? data.customer_name : '-',
      id: data.id,
      go_back_key: props.route.key,
    });
  };

  const onExport = async () => {
    setExporting(true);
    try {
      if (!filterExport.from_date || !filterExport.to_date) {
        dispatch(
          showAlert({
            show: true,
            title: 'Error',
            description: 'Filter date harus diisi',
            isConfirm: false,
          }),
        );
        setExporting(false);
        return;
      }
      const payload = {
        from_date: { $gte: filterExport.from_date },
        to_date: { $lte: filterExport.to_date },
        filename: `grn-${userData?._id}-${Date.now()}.csv`,
      };
      await exportCsvApi('lpb', payload, {
        isDownload: true,
        message: 'Data has been export.',
        fileTitle: 'grn',
      });
      setExporting(false);
    } catch (error) {
      dispatch(
        showAlert({
          show: true,
          title: 'Error',
          description:
            error?.response?.data?.message || 'A system error occurred',
          isConfirm: false,
        }),
      );
      setExporting(false);
    }
  };

  const getCarriers = async ({ val = null, skip = 0 }) => {
    if (skip == 0) {
      setCarriersLoading(true);
    }
    setCarriersParams({ ...carriersParams, loading: skip > 0, skip: skip });
    const payload = {
      params: {
        filter: {},
        skip: skip,
        limit: carriersParams.limit,
      },
    };

    const response = await getLpbCarrier.request(payload);
    if (response.status == 200 && response?.data?.data?.length) {
      const old_result = JSON.parse(JSON.stringify(carriers));
      const result = response.data.data.map((item) => {
        if (item._id) {
          item.id = item._id;
        }
        return item;
      });
      let finalResult = result;
      if (skip > 0) {
        finalResult = old_result.concat(result);
      }

      if (val) {
        // Manual search
        finalResult = finalResult.filter((c) =>
          c.name.toLowerCase().includes(val.toLowerCase()),
        );
      }

      if (userData?.role?.code == '3pl') {
        const carrier = userData?.carrier?.id;
        finalResult = finalResult.filter((c) => c.id == carrier);
      }

      setCarriers(finalResult);
      if (skip > 0) {
        setCarriersParams({ ...carriersParams, loading: false, skip: skip });
      }
    } else {
      if (skip <= 0) {
        setCarriersParams([]);
      }
      setCarriersParams({ ...carriersParams, loading: false, skip: skip });
    }
    setCarriersLoading(false);
  };

  // ── render ──
  return (
    <Layout.Container>
      {isWeb ? (
        <Layout.Title title="Goods Received Note">
          {showExportButton ? (
            <Button
              type="primary"
              rounded={true}
              buttonStyle={{ width: 90, marginRight: 10 }}
              title={
                <SociollaText
                  type="medium"
                  style={{ fontSize: 14, color: Colors.white }}>
                  Export
                </SociollaText>
              }
              onPress={() => {
                dispatch(
                  showPopup({
                    name: 'showLPBExport',
                    value: true,
                  }),
                );
              }}
            />
          ) : (
            false
          )}
        </Layout.Title>
      ) : (
        false
      )}
      {isWeb ? (
        <Table
          filterName={scope}
          name="lpb"
          structures={tableStructure}
          loading={list.isLoading}
          refetching={list.isFetching}
          data={rows}
          searchKey="keyword"
          searchPlaceholder="Search by SO number, DO number, GRN number, customer name..."
          searchValue={searchValue}
          searchWidth={460}
          hasNoHide={true}
          hasCallback={true}
          totalData={count.data}
          skip={params.skip}
          limit={params.limit}
          onClickRow={({ data }) => goToDetail(data)}
        />
      ) : (
        <TableMobile
          filterName={scope}
          searchKey="keyword"
          placeholder="Search by SO number / DO number / GRN number / customer name"
          searchValue={searchValue}
          loading={list.isLoading}
          refetching={list.isFetching}
          loadingLoadMore={list.isFetchingNextPage}
          hasNextPage={list.hasNextPage}
          onLoadMore={list.fetchNextPage}
          onRefresh={list.refetch}
          data={rows}
          totalData={count.data}
          renderItem={({ data, index }) => (
            <TouchableOpacity
              activeOpacity={0.6}
              style={styles.skpItem}
              onPress={() => goToDetail(data)}
              key={index}>
              <View
                style={[styles.skpItemSection, { alignItems: 'flex-start' }]}>
                <SociollaText
                  type="medium"
                  style={{ fontSize: 15, width: fullWidth - 140 }}>
                  {data.do_number ? data.do_number : '-'}
                </SociollaText>
                <StatusBadge status={data.status} />
              </View>
              <SociollaText
                style={{
                  marginTop: 12,
                  marginBottom: 10,
                  fontSize: 14,
                  color: Colors.grey2,
                }}>
                {data.channel ? data.channel : '-'} -{' '}
                {data.sale_team ? data.sale_team : '-'}
              </SociollaText>
              <View style={styles.skpItemSection}>
                <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                  <Image
                    style={{
                      width: 23,
                      height: 16,
                      marginLeft: -5,
                      marginRight: 5,
                    }}
                    width={23}
                    height={16}
                    svg={true}
                    source={require('@assets/img/icons/menu/calendar.svg')}
                  />
                  <SociollaText style={{ fontSize: 12, color: Colors.grey2 }}>
                    {data.created_at ? parseDate(data.created_at) : '-'}
                  </SociollaText>
                </View>
                <SociollaText
                  type="medium"
                  style={{ fontSize: 14, color: Colors.blue3 }}>
                  {data.customer_name ? data.customer_name : ''}
                </SociollaText>
              </View>
            </TouchableOpacity>
          )}
        />
      )}
      {/* Filter Container */}
      <Layout.Filter
        name="lpb"
        filterName={scope}
        isLocalVariable={true}
        hasFilterData={Object.keys(draftFilter).length > 0}
        onReset={() => {
          if (list.isFetching) {
            return;
          }
          setDraftFilter({});
          setDraftCarrier('');
          setDraftSalesTeam('');
          dispatch(paramsReseted({ key: scope }));
        }}
        onApply={() => {
          if (list.isFetching) {
            return;
          }
          dispatch(filterAdded({ key: scope, filter: draftFilter }));
          dispatch(showPopup({ name: 'showFilter', value: false }));
        }}>
        <Layout.FilterItem>
          <SociollaText style={{ fontSize: 13, marginBottom: 10 }}>
            Periode Created At
          </SociollaText>
          <View style={{ width: '100%', flexDirection: 'row' }}>
            <DatePicker
              label="Start Date"
              name="from_date"
              style={{
                marginRight: isWeb ? 10 : 0,
                width: isWeb ? 'auto' : '50%',
              }}
              disableAfterDate={draftFilter.to_date?.$lte}
              value={draftFilter.from_date?.$gte}
              onChange={(value) => draftDateHandler(value, 'from_date')}
            />
            <DatePicker
              label="End Date"
              name="to_date"
              style={{ width: isWeb ? 'auto' : '50%' }}
              value={draftFilter.to_date?.$lte}
              disableBeforeDate={draftFilter.from_date?.$gte}
              onChange={(value) => draftDateHandler(value, 'to_date')}
            />
          </View>
        </Layout.FilterItem>
        <View style={{ width: '100%', flexDirection: 'column' }}>
          <Layout.FilterItem>
            <SociollaText style={{ fontSize: 13, marginBottom: 10 }}>
              Carrier
            </SociollaText>
            <SelectBox
              label={'Carrier'}
              hideLabel
              options={carriers}
              multiple={false}
              value={draftCarrier}
              placeholder="Select Carrier"
              trackBy="id"
              labelKey="name"
              formLabelKey="name"
              loading={carriersLoading}
              withCallback={true}
              withLoadMore
              limit={carriersParams.limit}
              loadMoreEmpty={true}
              loadingCallback={carriersParams.loading}
              onLoadMore={(keyword) => {
                getCarriers({
                  val: keyword,
                  skip: carriersParams.skip + carriersParams.limit,
                });
              }}
              searchCallback={(keyword) => {
                getCarriers({
                  val: keyword,
                });
              }}
              onReady={() => {
                getCarriers({});
              }}
              onChange={(value) => {
                setDraftCarrier(value);
                setDraftFilter((f) => ({ ...f, carriers: value }));
              }}
            />
            <SociollaText style={{ fontSize: 13, marginBottom: 10 }}>
              Sales Team
            </SociollaText>
            <SalesTeamSearch
              value={draftSalesTeam}
              placeholder="Select Sales Team"
              multiple={true}
              customFilter={salesTeamFilter(userData)}
              hideLabel
              onChange={(value) => {
                setDraftSalesTeam(value);
                setDraftFilter((f) => ({ ...f, sales_team: value }));
              }}
            />
          </Layout.FilterItem>
        </View>
        <Layout.FilterItem>
          <SociollaText style={{ fontSize: 13, marginBottom: 10 }}>
            Status
          </SociollaText>
          <StatusPicker
            label=""
            options={statuses}
            value={draftFilter['status']}
            trackBy="value"
            labelKey="name"
            minWidth={80}
            onChange={(val) =>
              setDraftFilter((f) => ({ ...f, status: val.value }))
            }
          />
        </Layout.FilterItem>
      </Layout.Filter>
      <ModalFull
        title="Export GRN"
        show={showLPBExport}
        noTranslate={true}
        onClose={() =>
          dispatch(
            showPopup({
              name: 'showLPBExport',
              value: false,
            }),
          )
        }>
        <View
          style={{
            height: isWeb
              ? 'calc(100vh - 150px)'
              : dimension.height - (isAndroid ? 140 : 110),
          }}>
          <ScrollView contentContainerStyle={{ width: '100%' }}>
            <Layout.FormWrapper style={{ padding: 15, height: '100%' }}>
              <Layout.FormGroup lastItem>
                <SociollaText style={{ fontSize: 13, marginBottom: 10 }}>
                  Periode Created At
                </SociollaText>
                <View
                  style={{
                    width: '100%',
                    flexDirection: isWeb ? 'row' : 'column',
                    justifyContent: 'space-between',
                  }}>
                  <DatePicker
                    label="From Date *"
                    name="created_at"
                    style={{ width: isWeb ? '49%' : '100%' }}
                    value={filterExport.from_date}
                    onChange={(value) => {
                      setFilterExport({
                        ...filterExport,
                        from_date: formatISO(startOfDay(value)),
                      });
                    }}
                  />
                  <DatePicker
                    label="To Date *"
                    name="created_at"
                    style={{ width: isWeb ? '49%' : '100%' }}
                    value={filterExport.to_date}
                    disableBeforeDate={
                      filterExport.from_date
                        ? filterExport.from_date
                        : addDays(new Date(), 1)
                    }
                    onChange={(value) => {
                      setFilterExport({
                        ...filterExport,
                        to_date: formatISO(endOfDay(value)),
                      });
                    }}
                  />
                </View>
              </Layout.FormGroup>
            </Layout.FormWrapper>
          </ScrollView>
          <View
            style={{
              width: '100%',
              flexDirection: 'row',
              alignItems: 'flex-end',
              justifyContent: isWeb ? 'end' : 'space-between',
              paddingVertical: isWeb ? 20 : 15,
              paddingHorizontal: isWeb ? 20 : 15,
              backgroundColor: Colors.white,
              ...(!isWeb
                ? {
                    position: isMobileWeb ? 'fixed' : 'absolute',
                    bottom: 0,
                    right: 0,
                    left: 0,
                  }
                : {}),
            }}>
            <Button
              rounded={true}
              type="primary"
              buttonStyle={{
                width: isWeb
                  ? 100
                  : dimension.width / 2 - (isMobileWeb ? 20 : 25),
              }}
              disabledStyle={{ width: 90 }}
              disabled={exporting}
              title={
                <SociollaText
                  type="medium"
                  style={{ fontSize: 14, color: Colors.white }}>
                  {exporting ? 'Loading...' : 'Export'}
                </SociollaText>
              }
              onPress={() =>
                onExport().then(() => {
                  dispatch(
                    showPopup({
                      name: 'showLPBExport',
                      value: false,
                    }),
                  );
                })
              }
            />
          </View>
        </View>
      </ModalFull>
    </Layout.Container>
  );
};

const styles = StyleSheet.create({
  skpItem: {
    width: '100%',
    paddingVertical: 20,
    paddingHorizontal: 10,
    backgroundColor: Colors.white,
    borderBottomWidth: 1,
    borderBottomColor: Colors.grey0,
  },
  skpItemSection: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    width: fullWidth - 20,
  },
});

export default LPBScreen;
